import { apiClient } from "@/lib/apiClient";

const BASE = "/api/admin/reports/settings";

function unwrap<T>(payload: unknown): T {
  const body = payload as { data?: T } | T;
  if (body && typeof body === "object" && "data" in (body as Record<string, unknown>)) {
    return (body as { data: T }).data;
  }
  return body as T;
}

/**
 * Liste yanıtlarını normalize eder — backend interceptor'ı `{ items, count }`
 * döndüren uçları açıp `data`yı doğrudan diziye çeviriyor (bkz. report-settings.api.ts).
 */
function unwrapList<T>(payload: unknown): T[] {
  const inner = unwrap<unknown>(payload);
  if (Array.isArray(inner)) return inner as T[];
  const items = (inner as { items?: unknown })?.items;
  return Array.isArray(items) ? (items as T[]) : [];
}

export type CustomReportFilterKey =
  | "dateFrom"
  | "dateTo"
  | "departmentID"
  | "search"
  | "status";

export interface CustomReportField {
  key: string;
  label: string;
  type: "string" | "number" | "date" | "boolean";
}

export interface CustomReportSource {
  key: string;
  label: string;
  description: string;
  supportedFilters: CustomReportFilterKey[];
  sortableFields: string[];
  fields: CustomReportField[];
}

export interface CustomReportDefinition {
  id: string;
  name: string;
  description: string | null;
  dataSource: string;
  columns: string[];
  filters: Record<string, unknown>;
  sortBy: string | null;
  sortOrder: "asc" | "desc";
  updatedAt: string;
}

export interface CustomReportRunResult {
  columns: CustomReportField[];
  rows: Record<string, unknown>[];
  total: number;
  page: number;
  limit: number;
}

export interface CustomReportDraft {
  name: string;
  description?: string;
  dataSource: string;
  columns: string[];
  filters?: Record<string, unknown>;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export const customReportsApi = {
  listSources: async (): Promise<CustomReportSource[]> => {
    const { data } = await apiClient.get(`${BASE}/custom-reports/sources`);
    return unwrapList<CustomReportSource>(data);
  },

  list: async (): Promise<CustomReportDefinition[]> => {
    const { data } = await apiClient.get(`${BASE}/custom-reports`);
    return unwrapList<CustomReportDefinition>(data);
  },

  save: async (draft: CustomReportDraft): Promise<CustomReportDefinition> => {
    const { data } = await apiClient.post(`${BASE}/custom-reports`, draft);
    return unwrap<CustomReportDefinition>(data);
  },

  /** Kaydetmeden çalıştır — builder'daki canlı önizleme. */
  preview: async (
    draft: CustomReportDraft & { page?: number; limit?: number },
  ): Promise<CustomReportRunResult> => {
    const { data } = await apiClient.post(`${BASE}/custom-reports/preview`, draft);
    return unwrap<CustomReportRunResult>(data);
  },

  run: async (
    id: string,
    params: { page?: number; limit?: number; dateFrom?: string; dateTo?: string; search?: string },
  ): Promise<CustomReportRunResult> => {
    const { data } = await apiClient.get(`${BASE}/custom-reports/${id}/run`, { params });
    return unwrap<CustomReportRunResult>(data);
  },

  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`${BASE}/custom-reports/${id}`);
  },
};
